/**
 * 어드민 — 포인트 교환 상품 관리
 *  - 상품 목록 (이름/포인트/재고/노출/순서)
 *  - 추가/수정 모달
 *  - 삭제 (확인 후 soft delete)
 *  - 순서는 sort_order 숫자 입력 — 작을수록 위
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, put, del } from '@/api/client';
import { Button } from '@/components/atoms/Button';
import { Badge } from '@/components/atoms/Badge';
import { Skeleton } from '@/components/atoms/Skeleton';
import { toast } from '@/store/toastStore';
import { Pencil, Trash2, Gift, ExternalLink } from 'lucide-react';

interface ExchangeItem {
  code: string;
  name: string;
  description: string | null;
  image_url: string | null;
  point_amount: number;
  type: 'gifticon' | 'premium';
  stock: number | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

type FormState = {
  code: string;
  name: string;
  description: string;
  image_url: string;
  point_amount: number;
  type: 'gifticon' | 'premium';
  stockUnlimited: boolean;
  stock: number;
  is_active: boolean;
  sort_order: number;
};

const EMPTY_FORM: FormState = {
  code: '',
  name: '',
  description: '',
  image_url: '',
  point_amount: 500,
  type: 'gifticon',
  stockUnlimited: true,
  stock: 0,
  is_active: true,
  sort_order: 0,
};

export function ExchangeItemsPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<ExchangeItem | null>(null);
  const [creating, setCreating] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'exchange-items'],
    queryFn: () => get<{ items: ExchangeItem[] }>('/admin/exchange/items'),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'exchange-items'] });
    qc.invalidateQueries({ queryKey: ['exchange', 'items'] });
  };

  const createMut = useMutation({
    mutationFn: (body: Omit<FormState, 'stockUnlimited'> & { stock: number | null }) =>
      post('/admin/exchange/items', body),
    onSuccess: () => {
      toast.success('상품을 추가했어요.');
      setCreating(false);
      invalidate();
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message ?? '추가 실패'),
  });

  const updateMut = useMutation({
    mutationFn: ({ code, body }: { code: string; body: Partial<Omit<FormState, 'code' | 'stockUnlimited'>> & { stock?: number | null } }) =>
      put(`/admin/exchange/items/${code}`, body),
    onSuccess: () => {
      toast.success('수정했어요.');
      setEditing(null);
      invalidate();
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message ?? '수정 실패'),
  });

  const deleteMut = useMutation({
    mutationFn: (code: string) => del(`/admin/exchange/items/${code}`),
    onSuccess: () => {
      toast.success('삭제했어요.');
      invalidate();
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message ?? '삭제 실패'),
  });

  return (
    <div className="w-full">
      <div className="mb-6 flex items-center justify-between gap-2">
        <h1 className="text-2xl font-extrabold">포인트 교환 상품 관리</h1>
        <Button onClick={() => setCreating(true)}>+ 새 상품</Button>
      </div>

      {/* 상품권 구매 바로가기 — 운영자가 기프티콘 발급용 상품권을 구매하는 외부 사이트 */}
      <a
        href="https://sendbee.co.kr/goods/goodsList"
        target="_blank"
        rel="noopener noreferrer"
        className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-[var(--color-primary)]/30 bg-[var(--color-primary)]/5 p-4 transition-colors hover:border-[var(--color-primary)]/60 hover:bg-[var(--color-primary)]/10"
      >
        <div className="flex items-center gap-3">
          <Gift className="h-6 w-6 shrink-0 text-[var(--color-primary)]" />
          <div className="min-w-0">
            <div className="font-bold text-[var(--color-text)]">상품권 구매</div>
            <div className="mt-0.5 text-xs text-[var(--color-text-sub)]">
              센드비(sendbee) · 아이디 <span className="font-semibold text-[var(--color-text)]">norigonggan</span> <span className="font-semibold text-[var(--color-text)]">!@#</span>
            </div>
          </div>
        </div>
        <span className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-[var(--color-primary)] px-3 py-2 text-sm font-semibold text-white">
          구매하러 가기 <ExternalLink className="h-4 w-4" />
        </span>
      </a>

      <div className="mb-4 rounded-md border border-[var(--color-border)] bg-[var(--color-info-bg)] p-3 text-xs text-[var(--color-info)]">
        sort_order 가 작을수록 위에 노출돼요. 재고는 비워두면 무제한(NULL), 0 이면 자동으로 노출에서 제외돼요.
        삭제는 soft-delete 라 과거 교환 내역은 안전하게 보존됩니다.
      </div>

      {isLoading ? (
        <Skeleton className="h-80" />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]">
          <table className="w-full min-w-[760px] text-sm">
            <thead className="bg-[var(--color-bg)] text-[var(--color-text-sub)]">
              <tr>
                <th className="p-3 text-left">순서</th>
                <th className="p-3 text-left">코드</th>
                <th className="p-3 text-left">상품명</th>
                <th className="p-3 text-right">포인트</th>
                <th className="p-3 text-right">재고</th>
                <th className="p-3 text-left">타입</th>
                <th className="p-3 text-left">노출</th>
                <th className="p-3 text-right">관리</th>
              </tr>
            </thead>
            <tbody>
              {data?.items.map((it) => (
                <tr key={it.code} className="border-t border-[var(--color-border)]">
                  <td className="p-3">{it.sort_order}</td>
                  <td className="p-3 font-mono text-xs">{it.code}</td>
                  <td className="p-3">
                    <div className="font-semibold">{it.name}</div>
                    {it.description && (
                      <div className="mt-0.5 text-xs text-[var(--color-text-sub)]">{it.description}</div>
                    )}
                  </td>
                  <td className="p-3 text-right tabular-nums">{it.point_amount.toLocaleString()}P</td>
                  <td className="p-3 text-right">
                    {it.stock === null ? (
                      <Badge variant="outline">무제한</Badge>
                    ) : it.stock === 0 ? (
                      <Badge variant="fall">품절</Badge>
                    ) : (
                      <span className="tabular-nums">{it.stock}</span>
                    )}
                  </td>
                  <td className="p-3">
                    <Badge variant={it.type === 'premium' ? 'default' : 'outline'}>
                      {it.type === 'premium' ? '프리미엄' : '기프티콘'}
                    </Badge>
                  </td>
                  <td className="p-3">
                    {it.is_active ? (
                      <Badge variant="rise">노출</Badge>
                    ) : (
                      <Badge variant="default">숨김</Badge>
                    )}
                  </td>
                  <td className="p-3 text-right">
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setEditing(it)} aria-label="수정">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        aria-label="삭제"
                        onClick={() => {
                          if (window.confirm(`"${it.name}" 상품을 삭제할까요?\n(과거 교환 내역은 그대로 유지됩니다)`)) {
                            deleteMut.mutate(it.code);
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {data?.items.length === 0 && (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-sm text-[var(--color-text-sub)]">
                    등록된 상품이 없어요. "+ 새 상품" 으로 첫 상품을 추가하세요.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {(creating || editing) && (
        <ExchangeItemFormModal
          initial={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSubmit={(form) => {
            const payload = {
              ...form,
              stock: form.stockUnlimited ? null : Math.max(0, Number(form.stock) || 0),
            } as any;
            delete payload.stockUnlimited;
            if (editing) {
              const { code, ...rest } = payload;
              updateMut.mutate({ code: editing.code, body: rest });
            } else {
              createMut.mutate(payload);
            }
          }}
          submitting={createMut.isPending || updateMut.isPending}
        />
      )}
    </div>
  );
}

function ExchangeItemFormModal(props: {
  initial: ExchangeItem | null;
  onClose: () => void;
  onSubmit: (form: FormState) => void;
  submitting: boolean;
}) {
  const { initial, onClose, onSubmit, submitting } = props;
  const isEdit = !!initial;

  const [form, setForm] = useState<FormState>(() =>
    initial
      ? {
          code: initial.code,
          name: initial.name,
          description: initial.description ?? '',
          image_url: initial.image_url ?? '',
          point_amount: initial.point_amount,
          type: initial.type,
          stockUnlimited: initial.stock === null,
          stock: initial.stock ?? 0,
          is_active: initial.is_active,
          sort_order: initial.sort_order,
        }
      : EMPTY_FORM,
  );

  const update = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('상품명을 입력해주세요.');
    if (!isEdit && !/^[A-Za-z0-9_]{2,30}$/.test(form.code)) {
      return toast.error('상품 코드는 영문/숫자/언더스코어 2~30자입니다.');
    }
    if (form.point_amount < 1) return toast.error('포인트는 1 이상이어야 합니다.');
    onSubmit({ ...form, code: form.code.toUpperCase() });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-[var(--color-surface)] p-6 shadow-xl"
      >
        <h2 className="mb-4 text-lg font-bold">
          {isEdit ? '상품 수정' : '새 상품 추가'}
        </h2>

        <div className="space-y-3">
          <Field label="상품 코드 (영문 대문자/숫자/_)">
            <input
              type="text"
              value={form.code}
              onChange={(e) => update('code', e.target.value)}
              disabled={isEdit}
              placeholder="예: STARBUCKS_AMERICANO"
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 font-mono text-sm disabled:opacity-60"
              maxLength={30}
            />
          </Field>
          <Field label="상품명">
            <input
              type="text"
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
              placeholder="예: 스타벅스 아메리카노"
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm"
              maxLength={100}
              required
            />
          </Field>
          <Field label="설명">
            <textarea
              value={form.description}
              onChange={(e) => update('description', e.target.value)}
              placeholder="간단한 설명 (선택)"
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm"
              rows={2}
              maxLength={1000}
            />
          </Field>
          <Field label="상품 이미지 URL">
            <input
              type="text"
              value={form.image_url}
              onChange={(e) => update('image_url', e.target.value)}
              placeholder="https://..."
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm"
              maxLength={1000}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="필요 포인트">
              <input
                type="number"
                value={form.point_amount}
                onChange={(e) => update('point_amount', Number(e.target.value) || 0)}
                min={1}
                className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm tabular-nums"
                required
              />
            </Field>
            <Field label="타입">
              <select
                value={form.type}
                onChange={(e) => update('type', e.target.value as 'gifticon' | 'premium')}
                className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm"
              >
                <option value="gifticon">기프티콘</option>
                <option value="premium">프리미엄</option>
              </select>
            </Field>
          </div>

          <div className="rounded-md border border-[var(--color-border)] p-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.stockUnlimited}
                onChange={(e) => update('stockUnlimited', e.target.checked)}
              />
              재고 무제한 (NULL)
            </label>
            {!form.stockUnlimited && (
              <Field label="재고 수량" className="mt-2">
                <input
                  type="number"
                  value={form.stock}
                  onChange={(e) => update('stock', Math.max(0, Number(e.target.value) || 0))}
                  min={0}
                  className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm tabular-nums"
                />
              </Field>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="노출 순서 (낮을수록 위)">
              <input
                type="number"
                value={form.sort_order}
                onChange={(e) => update('sort_order', Math.max(0, Number(e.target.value) || 0))}
                min={0}
                className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm tabular-nums"
              />
            </Field>
            <Field label="노출 여부">
              <label className="flex h-[42px] items-center gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 text-sm">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => update('is_active', e.target.checked)}
                />
                활성
              </label>
            </Field>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
            취소
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? '저장 중...' : isEdit ? '수정' : '추가'}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field(props: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <label className={`block ${props.className ?? ''}`}>
      <span className="mb-1 block text-xs font-semibold text-[var(--color-text-sub)]">{props.label}</span>
      {props.children}
    </label>
  );
}
